import type { Extension, Practitioner, Questionnaire, QuestionnaireResponse, Reference } from '@medplum/fhirtypes';
import {
  CHART_DOCUMENT_LIFECYCLE_TAG_SYSTEM,
  CHART_DOCUMENT_SUPERSEDED_TAG_CODE,
  CLIENT_ASSIGNED_QR_TAG_CODE,
  CLIENT_ASSIGNED_QR_TAG_SYSTEM,
  HISTORICAL_MIGRATION_TAG_CODES,
  HISTORICAL_MIGRATION_TAG_SYSTEM,
  ROI_CONSENT_REF_EXT,
  TEMPLATE_CATEGORY_SYSTEM,
} from '@unify/fhir-model/constants';
import { getTemplateCategory } from '@unify/fhir-model/resources/questionnaire';
import { EPISODE_OF_CARE_CONTEXT_EXTENSION } from '../caseloadFhirConstants';
import type { SignatureInfo } from '../../pages/appointments/fhirNoteMapping';
import { practitionerPreferredReference } from '../loadPractitionerPreferredLabels';
import {
  CLIENT_SIGNATURE_EXT_URL,
  DETAILED_STATUS_EXT_URL,
  getSignatureFromQR,
  practitionerRequiresCosignature,
  PROVIDER_SIGNATURE_EXT_URL,
  setSignatureOnQR,
  SUPERVISOR_SIGNATURE_EXT_URL,
} from '../../pages/appointments/fhirNoteMapping';
import { collectClientEditableLinkIds, chartDocumentRequiresSupervisorCosign } from './chartDocumentTemplates';
import { indexResponseItemsByLinkId } from '../../pages/appointments/utils/questionnaireResponseShape';

export const CHART_DOCUMENT_STATUS_EXT = 'https://unify.health/fhir/StructureDefinition/chart-document-status';
export const CHART_DOCUMENT_PROVIDER_LOCKED_ITEMS_EXT =
  'https://unify.health/fhir/StructureDefinition/chart-document-provider-locked-items';
export const CHART_DOCUMENT_CLIENT_SIGN_REQUIRED_EXT =
  'https://unify.health/fhir/StructureDefinition/chart-document-client-sign-required';
export const CHART_DOCUMENT_PROVIDER_SIGN_REQUIRED_EXT =
  'https://unify.health/fhir/StructureDefinition/chart-document-provider-sign-required';

export type ChartDocumentStatus = 'draft' | 'pending-client' | 'completed' | 'cancelled' | 'error';

export const CHART_DOCUMENT_STATUS_LABELS: Record<ChartDocumentStatus, string> = {
  draft: 'Draft',
  'pending-client': 'Pending client',
  completed: 'Completed',
  cancelled: 'Cancelled',
  error: 'Error',
};

function upsertExtension(extensions: Extension[] | undefined, ext: Extension): Extension[] {
  const rest = (extensions ?? []).filter((e) => e.url !== ext.url);
  return [...rest, ext];
}

export function isChartDocumentQuestionnaireResponse(qr: QuestionnaireResponse): boolean {
  return qr.extension?.some((e) => e.url === CHART_DOCUMENT_STATUS_EXT) ?? false;
}

/** Contact ROI forms are patient-scoped (not episode-scoped) chart documents. */
export function isContactRoiQuestionnaireResponse(qr: QuestionnaireResponse): boolean {
  return qr.extension?.some((e) => e.url === ROI_CONSENT_REF_EXT) ?? false;
}

/**
 * ENG-3764 D3: a chart document a re-send replaced (chart status `cancelled`, QR `stopped`, the `superseded` lifecycle
 * tag). The chart hides it; a cancelled send without the tag stays listed.
 */
export function isSupersededChartDocument(qr: QuestionnaireResponse): boolean {
  return (
    qr.status === 'stopped'
    && getChartDocumentStatus(qr) === 'cancelled'
    && (qr.meta?.tag?.some((t) => t.system === CHART_DOCUMENT_LIFECYCLE_TAG_SYSTEM && t.code === CHART_DOCUMENT_SUPERSEDED_TAG_CODE) ?? false)
  );
}

export function getChartDocumentEpisodeId(qr: QuestionnaireResponse): string | undefined {
  const ext = qr.extension?.find((e) => e.url === EPISODE_OF_CARE_CONTEXT_EXTENSION);
  return ext?.valueReference?.reference?.replace('EpisodeOfCare/', '');
}

export function applyChartDocumentEpisode(
  qr: QuestionnaireResponse,
  episodeId: string | undefined,
): QuestionnaireResponse {
  const rest = (qr.extension ?? []).filter((e) => e.url !== EPISODE_OF_CARE_CONTEXT_EXTENSION);
  if (!episodeId) {
    return { ...qr, extension: rest.length ? rest : undefined };
  }
  return {
    ...qr,
    extension: [
      ...rest,
      {
        url: EPISODE_OF_CARE_CONTEXT_EXTENSION,
        valueReference: { reference: `EpisodeOfCare/${episodeId}` },
      },
    ],
  };
}

export function isChartDocumentClientSignatureRequired(qr: QuestionnaireResponse): boolean {
  const ext = qr.extension?.find((e) => e.url === CHART_DOCUMENT_CLIENT_SIGN_REQUIRED_EXT);
  return ext?.valueBoolean === true;
}

export function getChartDocumentClientSignature(qr: QuestionnaireResponse): SignatureInfo | undefined {
  return getSignatureFromQR(qr, CLIENT_SIGNATURE_EXT_URL);
}

export function applyClientSignature(
  qr: QuestionnaireResponse,
  signature: SignatureInfo,
): QuestionnaireResponse {
  return setSignatureOnQR(qr, CLIENT_SIGNATURE_EXT_URL, signature);
}

export function getChartDocumentStatus(qr: QuestionnaireResponse): ChartDocumentStatus {
  const ext = qr.extension?.find((e) => e.url === CHART_DOCUMENT_STATUS_EXT);
  const code = ext?.valueCode as ChartDocumentStatus | undefined;
  if (code && code in CHART_DOCUMENT_STATUS_LABELS) return code;
  if (qr.status === 'stopped') return 'cancelled';
  if (qr.status === 'completed') return 'completed';
  return 'draft';
}

export type ChartDocumentClinicalNoteStatus = 'Draft' | 'Signed' | 'Pending co-signature' | 'Complete';

/** SP historical migration QuestionnaireResponses (native treatment plans, etc.). */
export function isHistoricalMigrationQuestionnaireResponse(qr: QuestionnaireResponse): boolean {
  return (
    qr.meta?.tag?.some(
      (t) =>
        t.system === HISTORICAL_MIGRATION_TAG_SYSTEM &&
        t.code === HISTORICAL_MIGRATION_TAG_CODES.historical,
    ) ?? false
  );
}

/**
 * Chart timeline status — only treatment-planning templates use supervisor cosign rules.
 * Other completed chart documents show Complete once the chart-document workflow is done.
 * Historical migration imports never show Draft / Pending co-signature (not signable in Unify).
 */
export function deriveChartDocumentClinicalNoteStatus(
  qr: QuestionnaireResponse,
  questionnaire: Questionnaire | undefined,
  author: Practitioner | undefined,
): ChartDocumentClinicalNoteStatus {
  if (isHistoricalMigrationQuestionnaireResponse(qr)) {
    const hasProv = !!getSignatureFromQR(qr, PROVIDER_SIGNATURE_EXT_URL);
    const hasSup = !!getSignatureFromQR(qr, SUPERVISOR_SIGNATURE_EXT_URL);
    if (hasProv || hasSup) return 'Signed';
    return 'Complete';
  }

  const chartStatus = getChartDocumentStatus(qr);
  if (chartStatus === 'draft' || chartStatus === 'pending-client' || chartStatus === 'cancelled') {
    return 'Draft';
  }
  if (chartStatus !== 'completed') {
    return 'Draft';
  }

  if (questionnaire && chartDocumentRequiresSupervisorCosign(questionnaire)) {
    const requiresCosign = practitionerRequiresCosignature(author);
    const hasProv = !!getSignatureFromQR(qr, PROVIDER_SIGNATURE_EXT_URL);
    const hasSup = !!getSignatureFromQR(qr, SUPERVISOR_SIGNATURE_EXT_URL);
    if (hasSup) return 'Signed';
    if (hasProv && requiresCosign) return 'Pending co-signature';
    if (hasProv) return 'Signed';
  }

  return 'Complete';
}

/**
 * Patient AccessPolicy (`patientSelfResources`) only allows reading in-progress
 * QuestionnaireResponses carrying the `client-assigned` tag, so a chart document
 * sent to the client gets that tag and loses it on every other status (P2-F).
 *
 * The synthetic `forms` category tag is the assignment marker the `task-events`
 * listener and the staff assignment QR rows key on (ENG-3764 D25). Chart documents
 * copy the template category onto the QR, so treatment-planning (and similar) docs
 * sent to the client get it too and drop it again when they leave `pending-client`.
 */
const PATIENT_PORTAL_QR_CATEGORY_CODES = new Set(['forms']);

function qrHasPatientPortalCategoryTag(qr: QuestionnaireResponse): boolean {
  return (qr.meta?.tag ?? []).some(
    (t) => t.system === TEMPLATE_CATEGORY_SYSTEM && t.code != null && PATIENT_PORTAL_QR_CATEGORY_CODES.has(t.code),
  );
}

function withPatientPortalReadableCategoryTag(qr: QuestionnaireResponse): QuestionnaireResponse {
  if (qrHasPatientPortalCategoryTag(qr)) return qr;
  return {
    ...qr,
    meta: {
      ...(qr.meta ?? {}),
      tag: [...(qr.meta?.tag ?? []), { system: TEMPLATE_CATEGORY_SYSTEM, code: 'forms' }],
    },
  };
}

/** Drop the synthetic `forms` tag if the QR still has a non-portal native category. */
function withoutSyntheticPatientPortalCategoryTag(qr: QuestionnaireResponse): QuestionnaireResponse {
  const tags = qr.meta?.tag ?? [];
  const hasNativeNonPortalCategory = tags.some(
    (t) => t.system === TEMPLATE_CATEGORY_SYSTEM && t.code != null && !PATIENT_PORTAL_QR_CATEGORY_CODES.has(t.code),
  );
  if (!hasNativeNonPortalCategory) return qr;
  const nextTags = tags.filter(
    (t) => !(t.system === TEMPLATE_CATEGORY_SYSTEM && t.code === 'forms'),
  );
  return {
    ...qr,
    meta: {
      ...(qr.meta ?? {}),
      tag: nextTags,
    },
  };
}

function isClientAssignedTag(t: { system?: string; code?: string }): boolean {
  return t.system === CLIENT_ASSIGNED_QR_TAG_SYSTEM && t.code === CLIENT_ASSIGNED_QR_TAG_CODE;
}

function withClientAssignedTag(qr: QuestionnaireResponse): QuestionnaireResponse {
  if ((qr.meta?.tag ?? []).some(isClientAssignedTag)) return qr;
  return {
    ...qr,
    meta: {
      ...(qr.meta ?? {}),
      tag: [...(qr.meta?.tag ?? []), { system: CLIENT_ASSIGNED_QR_TAG_SYSTEM, code: CLIENT_ASSIGNED_QR_TAG_CODE }],
    },
  };
}

function withoutClientAssignedTag(qr: QuestionnaireResponse): QuestionnaireResponse {
  const tags = qr.meta?.tag ?? [];
  if (!tags.some(isClientAssignedTag)) return qr;
  const nextTags = tags.filter((t) => !isClientAssignedTag(t));
  return {
    ...qr,
    meta: {
      ...(qr.meta ?? {}),
      tag: nextTags,
    },
  };
}

export function setChartDocumentStatus(
  qr: QuestionnaireResponse,
  status: ChartDocumentStatus,
): QuestionnaireResponse {
  let next: QuestionnaireResponse = {
    ...qr,
    extension: upsertExtension(qr.extension, {
      url: CHART_DOCUMENT_STATUS_EXT,
      valueCode: status,
    }),
  };

  if (status === 'completed') {
    next = {
      ...next,
      status: 'completed',
      extension: upsertExtension(next.extension, {
        url: DETAILED_STATUS_EXT_URL,
        valueCode: 'signed',
      }),
    };
    next = withoutClientAssignedTag(withoutSyntheticPatientPortalCategoryTag(next));
  } else if (status === 'cancelled') {
    next = { ...next, status: 'stopped' };
    next = withoutClientAssignedTag(withoutSyntheticPatientPortalCategoryTag(next));
  } else if (status === 'draft') {
    next = {
      ...next,
      status: 'in-progress',
      extension: upsertExtension(next.extension, {
        url: DETAILED_STATUS_EXT_URL,
        valueCode: 'draft',
      }),
    };
    next = withoutClientAssignedTag(withoutSyntheticPatientPortalCategoryTag(next));
  } else if (status === 'pending-client') {
    next = {
      ...next,
      status: 'in-progress',
      extension: upsertExtension(next.extension, {
        url: DETAILED_STATUS_EXT_URL,
        valueCode: 'pending-signature',
      }),
    };
    next = withClientAssignedTag(withPatientPortalReadableCategoryTag(next));
  }

  return next;
}

export function getProviderLockedLinkIds(qr: QuestionnaireResponse): Set<string> {
  const ext = qr.extension?.find((e) => e.url === CHART_DOCUMENT_PROVIDER_LOCKED_ITEMS_EXT);
  if (!ext?.valueString) return new Set();
  try {
    const parsed = JSON.parse(ext.valueString) as unknown;
    if (Array.isArray(parsed)) {
      return new Set(parsed.filter((v): v is string => typeof v === 'string'));
    }
  } catch {
    // ignore malformed extension
  }
  return new Set();
}

export function setProviderLockedLinkIds(
  qr: QuestionnaireResponse,
  linkIds: Iterable<string>,
): QuestionnaireResponse {
  return {
    ...qr,
    extension: upsertExtension(qr.extension, {
      url: CHART_DOCUMENT_PROVIDER_LOCKED_ITEMS_EXT,
      valueString: JSON.stringify([...linkIds]),
    }),
  };
}

export function getChartDocumentSignRequirements(qr: QuestionnaireResponse): {
  providerSignRequired: boolean;
  clientSignRequired: boolean;
  sendToClient: boolean;
} {
  const providerExt = qr.extension?.find((e) => e.url === CHART_DOCUMENT_PROVIDER_SIGN_REQUIRED_EXT);
  const clientExt = qr.extension?.find((e) => e.url === CHART_DOCUMENT_CLIENT_SIGN_REQUIRED_EXT);
  const status = getChartDocumentStatus(qr);
  return {
    providerSignRequired: providerExt?.valueBoolean === true,
    clientSignRequired: clientExt?.valueBoolean === true,
    sendToClient: status === 'pending-client' || status === 'completed',
  };
}

/** Copy the template library category tag onto the chart document QR for search/subscriptions. */
export function applyChartDocumentTemplateCategoryTag(
  qr: QuestionnaireResponse,
  template: Questionnaire | undefined,
): QuestionnaireResponse {
  if (!template) return qr;
  const category = getTemplateCategory(template);
  if (!category) return qr;

  const existingTags = (qr.meta?.tag ?? []).filter((t) => t.system !== TEMPLATE_CATEGORY_SYSTEM);
  const retagged: QuestionnaireResponse = {
    ...qr,
    meta: {
      ...(qr.meta ?? {}),
      tag: [...existingTags, { system: TEMPLATE_CATEGORY_SYSTEM, code: category }],
    },
  };
  // A sent chart document keeps the synthetic `forms` tag next to its native category (ENG-3764 D1): the listener's
  // Subscription and the client's read access both key on it, and the re-tag above just removed it.
  return getChartDocumentStatus(retagged) === 'pending-client' ? withPatientPortalReadableCategoryTag(retagged) : retagged;
}

export function applyChartDocumentSignSettings(
  qr: QuestionnaireResponse,
  settings: {
    providerSignRequired: boolean;
    clientSignRequired: boolean;
  },
): QuestionnaireResponse {
  let extension = qr.extension ?? [];
  extension = upsertExtension(extension, {
    url: CHART_DOCUMENT_PROVIDER_SIGN_REQUIRED_EXT,
    valueBoolean: settings.providerSignRequired,
  });
  extension = upsertExtension(extension, {
    url: CHART_DOCUMENT_CLIENT_SIGN_REQUIRED_EXT,
    valueBoolean: settings.clientSignRequired,
  });
  return { ...qr, extension };
}

/**
 * Preserve server-managed workflow links when saving a form snapshot that was
 * loaded before a bot added them.
 */
export function preserveChartDocumentBasedOn(
  latest: QuestionnaireResponse,
  pending: QuestionnaireResponse,
): QuestionnaireResponse {
  const references = [...(latest.basedOn ?? []), ...(pending.basedOn ?? [])];
  if (references.length === 0) return pending;

  const seen = new Set<string>();
  const basedOn = references.filter((reference) => {
    const key = reference.reference
      ?? `${reference.identifier?.system ?? ''}|${reference.identifier?.value ?? ''}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
  return { ...pending, basedOn };
}

export function buildInitialChartDocumentResponse(opts: {
  template: Questionnaire;
  patientId: string;
  episodeId?: string;
  providerSignRequired?: boolean;
  clientSignRequired?: boolean;
  author?: Practitioner | null;
  patientAccounts?: Reference[];
}): QuestionnaireResponse {
  const { template, patientId, episodeId, author, patientAccounts } = opts;
  const canonicalRef = template.url && template.version
    ? `${template.url}|${template.version}`
    : template.id
      ? `Questionnaire/${template.id}`
      : undefined;

  const extensions: Extension[] = [
    { url: CHART_DOCUMENT_STATUS_EXT, valueCode: 'draft' },
    { url: DETAILED_STATUS_EXT_URL, valueCode: 'draft' },
    {
      url: CHART_DOCUMENT_PROVIDER_SIGN_REQUIRED_EXT,
      valueBoolean: opts.providerSignRequired ?? false,
    },
    {
      url: CHART_DOCUMENT_CLIENT_SIGN_REQUIRED_EXT,
      valueBoolean: opts.clientSignRequired ?? false,
    },
  ];

  if (episodeId) {
    extensions.push({
      url: EPISODE_OF_CARE_CONTEXT_EXTENSION,
      valueReference: { reference: `EpisodeOfCare/${episodeId}` },
    });
  }

  const authorRef = author?.id ? practitionerPreferredReference(author) : undefined;

  const base: QuestionnaireResponse = {
    resourceType: 'QuestionnaireResponse',
    status: 'in-progress',
    questionnaire: canonicalRef,
    subject: { reference: `Patient/${patientId}` },
    ...(authorRef ? { author: authorRef, source: authorRef } : {}),
    authored: new Date().toISOString(),
    item: [],
    extension: extensions,
    ...(patientAccounts?.length ? { meta: { accounts: patientAccounts } } : {}),
  };

  return applyChartDocumentTemplateCategoryTag(base, template);
}

export function applyProviderSignature(
  qr: QuestionnaireResponse,
  signature: SignatureInfo,
): QuestionnaireResponse {
  return {
    ...qr,
    extension: upsertExtension(qr.extension, {
      url: PROVIDER_SIGNATURE_EXT_URL,
      valueString: JSON.stringify(signature),
    }),
  };
}

/** Clears provider and supervisor signatures when a signed chart document returns to draft. */
export function clearChartDocumentProviderSignatures(qr: QuestionnaireResponse): QuestionnaireResponse {
  const signatureUrls = [PROVIDER_SIGNATURE_EXT_URL, SUPERVISOR_SIGNATURE_EXT_URL];
  return {
    ...qr,
    extension: (qr.extension ?? []).filter((e) => !signatureUrls.includes(e.url)),
  };
}

/**
 * Clears the client signature when a chart document is sent to the client (again): an earlier signature belongs to
 * the content the client signed then, and the portal would otherwise seed its pad with it (ENG-3764 D3).
 */
export function clearChartDocumentClientSignature(qr: QuestionnaireResponse): QuestionnaireResponse {
  if (!qr.extension?.some((e) => e.url === CLIENT_SIGNATURE_EXT_URL)) return qr;
  return {
    ...qr,
    extension: qr.extension.filter((e) => e.url !== CLIENT_SIGNATURE_EXT_URL),
  };
}

/** LinkIds to lock for the client based on provider answers and template audience. */
export function computeProviderLockedLinkIds(
  questionnaire: Questionnaire,
  qr: QuestionnaireResponse,
): string[] {
  const clientLinkIds = new Set(collectClientEditableLinkIds(questionnaire));
  const locked = new Set<string>();

  for (const [linkId, item] of indexResponseItemsByLinkId(qr.item)) {
    if (clientLinkIds.has(linkId)) continue;
    if (item.answer?.length) {
      locked.add(linkId);
    }
  }

  return [...locked];
}

/**
 * Locked provider fields for the client portal — uses persisted extension when present,
 * otherwise derives from provider answers (e.g. documents sent before locks were stored).
 */
export function resolveProviderLockedLinkIdsForClientView(
  questionnaire: Questionnaire,
  qr: QuestionnaireResponse,
): Set<string> {
  const stored = getProviderLockedLinkIds(qr);
  if (stored.size > 0) return stored;
  return new Set(computeProviderLockedLinkIds(questionnaire, qr));
}
